import { useState } from "react";
import { ArrowRight, Lock, CheckCircle2 } from "lucide-react";
import { apiUtils } from "../../utils/apiUtils";
import PATHS from "../../constants/Paths";

/**
 * PilotApplicationForm - the single pilot-programme application form.
 *
 * Used by the landing-page modal and the /register page, so the fields, the
 * validation and the success copy stay identical wherever someone applies.
 * Posts to POST /api/waitlist (public).
 */

/**
 * `required: false` on the company is the point of this list, not an oversight.
 * Every field used to be required and one of them was Company, so a creator
 * applying to watch their own account had nothing to type and stopped there.
 * The label said "Work email" and the example answer below described an agency
 * monitoring a hundred people, which told them twice more that this was not
 * for them. The field stays because it is a useful qualifier when someone does
 * have one.
 */
const FIELDS = [
  { name: "name", label: "Full name", type: "text", placeholder: "Jordan Mae", autoComplete: "name", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "you@example.com", autoComplete: "email", required: true },
  { name: "companyName", label: "Company or agency", type: "text", placeholder: "Leave blank if it is just you", autoComplete: "organization", required: false },
];

const inputClass =
  "w-full rounded-lg border border-input px-3.5 py-2.5 text-foreground transition-shadow placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";

export default function PilotApplicationForm({ submitLabel = "Request early access", onSuccess }) {
  const [formData, setFormData] = useState({ name: "", email: "", companyName: "", useCase: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [done, setDone] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      const response = await apiUtils.post("/api/waitlist", formData);
      const data = response?.data ?? (typeof response?.json === "function" ? await response.json() : response);
      if (data?.success) {
        setDone(true);
        setFormData({ name: "", email: "", companyName: "", useCase: "" });
        if (onSuccess) onSuccess(data);
      } else {
        setStatus({ type: "error", message: data?.message || "Something went wrong. Please try again." });
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Something went wrong. Please try again.";
      setStatus({ type: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Terminal success state. Replaces the form rather than sitting above it, so
  // nobody re-submits an application that already went through.
  if (done) {
    return (
      <div className="rounded-xl border border-brand-200 bg-brand-50 p-5 text-center dark:bg-brand-500/10 dark:border-brand-500/30">
        <CheckCircle2 className="mx-auto h-9 w-9 text-brand-600" />
        <h3 className="mt-3 text-lg font-semibold text-foreground">Application received</h3>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Thanks - we review every application personally and will email you at the address
          you gave us. If you're approved you'll get a link to set your password.
        </p>
      </div>
    );
  }

  return (
    <>
      {status.message && (
        <div
          className={`mt-5 rounded-lg border p-3 text-sm ${
            status.type === "success"
              ? "border-brand-200 bg-brand-50 text-brand-800 dark:bg-brand-500/10 dark:border-brand-500/30 dark:text-brand-200"
              : "border-red-200 bg-red-50 text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200"
          }`}
          role="alert"
        >
          {status.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        {FIELDS.map((field) => (
          <div key={field.name}>
            <label htmlFor={`pilot-${field.name}`} className="mb-1.5 block text-sm font-medium text-foreground">
              {field.label}
              {!field.required && <span className="ml-1.5 font-normal text-muted-foreground">optional</span>}
            </label>
            <input
              id={`pilot-${field.name}`}
              type={field.type}
              name={field.name}
              autoComplete={field.autoComplete}
              value={formData[field.name]}
              onChange={handleInputChange}
              required={field.required}
              placeholder={field.placeholder}
              className={inputClass}
            />
          </div>
        ))}
        <div>
          <label htmlFor="pilot-useCase" className="mb-1.5 block text-sm font-medium text-foreground">
            What would you use RepCheck for?
          </label>
          <textarea
            id="pilot-useCase"
            name="useCase"
            value={formData.useCase}
            onChange={handleInputChange}
            required
            rows="3"
            placeholder="e.g. keeping an eye on my own account, or watching a roster of creators"
            className={`${inputClass} resize-none`}
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-brand-700 ${
            isSubmitting ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          {isSubmitting ? "Submitting…" : submitLabel}
          {!isSubmitting && <ArrowRight className="h-5 w-5" />}
        </button>
      </form>

      <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <Lock className="h-3.5 w-3.5" /> We take privacy seriously. Your details stay private.
      </p>
      <p className="mt-3 text-center text-xs leading-5 text-muted-foreground">
        By applying you agree to our{" "}
        <a href={PATHS.TERMS} target="_blank" rel="noreferrer" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">
          Terms of Service
        </a>{" "}
        and{" "}
        <a href={PATHS.PRIVACY} target="_blank" rel="noreferrer" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">
          Privacy Policy
        </a>
        .
      </p>
    </>
  );
}
